/** CODE 4 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container, Row, Form } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";
import axios from "axios";
import Loader from "pages/Separate/Loader";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import configure from "configure";
import zithasLogo from "../../../assets/images/zithas-dark-logo.png";
import sign from "../../../assets/images/signature.png";
import "../../../assets/css/Sales/Sales.css";
import "../../../assets/css/Sales/Contract.css";
import { FetchNodePostData } from "utils/common-fetchData";
import secureLocalStorage from "react-secure-storage";
import CreatableSelect from "react-select/creatable";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const CHANGE_REASON_OPTIONS = [
  { label: "Client requested additional scope", value: "additional_scope" },
  { label: "Revised timeline", value: "revised_timeline" },
  { label: "Pricing adjustment", value: "pricing_adjustment" },
  { label: "Resource change", value: "resource_change" },
  { label: "Reduced scope", value: "reduced_scope" },
  { label: "Other", value: "other" },
];

const colourStyles = {
  menuList: (s) => ({ ...s, background: "white" }),
  menu: (s) => ({ ...s, zIndex: 9999 }),
  control: (s) => ({ ...s, fontSize: "13px", minHeight: "36px" }),
  option: (s) => ({ ...s, fontSize: "13px" }),
};

const emptyMilestone = () => ({ name: "", revised_date: "" });
const emptyScope = () => ({ type: "add", description: "" });

// ─────────────────────────────────────────────────────────────────────────────
// Small shared UI pieces
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = ({ title, icon, children, action }) => (
  <div className="bg-white px-4 pt-4 pb-3 mb-3">
    <div
      className="d-flex align-items-center justify-content-between mb-3 pb-2"
      style={{ borderBottom: "2px solid #f4f0fb" }}
    >
      <div
        className="d-flex align-items-center gap-2"
        style={{
          fontSize: "12px",
          fontWeight: 600,
          color: "#495057",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {icon && (
          <i className={icon} style={{ color: "#556ee6", fontSize: "14px" }} />
        )}
        {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const FieldLabel = ({ children, required }) => (
  <label
    className="mb-1"
    style={{
      fontSize: "13px",
      color: "#495057",
      display: "block",
      fontWeight: 500,
    }}
  >
    {children}
    {required && <span className="text-danger ms-1">*</span>}
  </label>
);

const DirectionToggle = ({ value, onChange }) => (
  <div className="btn-group flex-shrink-0" role="group">
    <button
      type="button"
      className={`btn btn-sm ${
        value === "add" ? "btn-success" : "btn-outline-secondary"
      }`}
      style={{ fontSize: "11px", padding: "5px 10px", whiteSpace: "nowrap" }}
      onClick={() => onChange("add")}
    >
      + Add
    </button>
    <button
      type="button"
      className={`btn btn-sm ${
        value === "remove" ? "btn-danger" : "btn-outline-secondary"
      }`}
      style={{ fontSize: "11px", padding: "5px 10px", whiteSpace: "nowrap" }}
      onClick={() => onChange("remove")}
    >
      − Remove
    </button>
  </div>
);

const generateDocumentHTML = ({
  contract,
  amendmentNo,
  changeReason,
  effectiveDate,
  hoursDirection,
  hoursChange,
  feeDirection,
  feeChange,
  revisedValue,
  revisedHours,
  milestones,
  scopeItems,
  sym,
  logoBase64, // optional – if provided, embeds logo as data-uri
}) => {
  const contractValue = parseFloat(contract?.value) || 0;
  const currentHours = parseFloat(contract?.hours) || 0;
  const feeNum = parseFloat(feeChange) || 0;
  const hoursNum = parseFloat(hoursChange) || 0;

  const addedScopes = scopeItems.filter(
    (s) => s.type === "add" && s.description,
  );
  const removedScopes = scopeItems.filter(
    (s) => s.type === "remove" && s.description,
  );
  const allScopes = scopeItems.filter((s) => s.description);
  const validMilestones = milestones.filter((m) => m.name || m.revised_date);

  const amendLabel = `A${amendmentNo}`;
  const contractRef =
    contract?.contract_no || `ZT-${new Date().getFullYear()}-001`;

  let sectionNum = 1;
  const S = () => sectionNum++; // auto-incrementing section number

  // ── helpers ──
  const row = (label, value) =>
    `<tr><td style="padding:7px 12px;border:1px solid #ccc;font-size:15px;">${label}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:15px;text-align:right;">${value}</td></tr>`;

  const summaryRow = (label, value) =>
    `<tr style="background:#f8f8f8;"><td style="padding:7px 12px;border:1px solid #ccc;font-size:15px;font-weight:700;">${label}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:15px;font-weight:700;text-align:right;">${value}</td></tr>`;

  const tableHeader = (col1, col2) =>
    `<thead><tr><th style="background:#f2f2f2;border:1px solid #ccc;padding:7px 12px;font-size:14px;text-transform:uppercase;letter-spacing:0.03em;text-align:left;">${col1}</th><th style="background:#f2f2f2;border:1px solid #ccc;padding:7px 12px;font-size:14px;text-transform:uppercase;letter-spacing:0.03em;text-align:right;">${col2}</th></tr></thead>`;

  const sectionHeading = (text) =>
    `<p style="font-size:16px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;border-bottom:1px solid #ccc;padding-bottom:4px;margin:20px 0 8px 0;color:#333;">${text}</p>`;

  const para = (text) =>
    `<p style="font-size:15px;color:#333;margin:0 0 8px 0;line-height:1.7;">${text}</p>`;

  // ── logo ──
  const logoHtml = logoBase64
    ? `<img src="${logoBase64}" height="42" style="display:block;margin:0 auto 8px auto;" />`
    : `<p style="text-align:center;font-size:18px;font-weight:700;margin:0 0 8px 0;">Zithas Technologies Ltd</p>`;

  // ─── build document ───────────────────────────────────────────────────────

  let html = `
<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#333;line-height:1.7;max-width:100%;">

  ${logoHtml}

  <p style="text-align:center;font-size:17px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin:0 0 4px 0;">Contract Amendment No. ${amendLabel}</p>
  <p style="text-align:center;font-size:14px;color:#666;margin:0 0 4px 0;">Relating to Contract Ref: ${contractRef}</p>
  ${
    contract?.startDate
      ? `<p style="text-align:center;font-size:14px;color:#666;margin:0 0 4px 0;">Original Agreement Date: ${moment(
          contract.startDate,
        ).format("DD MMMM YYYY")}</p>`
      : ""
  }

  <hr style="border:none;border-top:1.5px solid #333;margin:14px 0;" />

  <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
    <tbody>
      <tr><td style="padding:4px 0;font-size:15px;font-weight:700;width:90px;vertical-align:top;">Client:</td><td style="padding:4px 0;font-size:15px;">${
        contract?.company_name || "[Client Name]"
      }</td></tr>
      <tr><td style="padding:4px 0;font-size:15px;font-weight:700;vertical-align:top;">Supplier:</td><td style="padding:4px 0;font-size:15px;">Zithas Technologies Ltd</td></tr>
      ${
        effectiveDate
          ? `<tr><td style="padding:4px 0;font-size:15px;font-weight:700;vertical-align:top;">Effective:</td><td style="padding:4px 0;font-size:15px;">${moment(
              effectiveDate,
            ).format("DD MMMM YYYY")}</td></tr>`
          : ""
      }
      ${
        changeReason
          ? `<tr><td style="padding:4px 0;font-size:15px;font-weight:700;vertical-align:top;">Reason:</td><td style="padding:4px 0;font-size:15px;">${changeReason.label}</td></tr>`
          : ""
      }
    </tbody>
  </table>

  ${sectionHeading(`${S()}. Purpose of this Amendment`)}
  ${para(
    `This Contract Amendment ("<strong>Amendment</strong>") is entered into by mutual agreement of the Parties to amend certain provisions of the above-mentioned Contract.`,
  )}
  ${para(
    `Except as expressly modified by this Amendment, all terms, conditions and obligations contained in the original Contract shall remain unchanged and continue in full force and effect.`,
  )}
`;

  // ── Scope + Description section (merged) ──
  if (allScopes.length > 0) {
    html += sectionHeading(`${S()}. Scope of Changes`);

    if (addedScopes.length > 0) {
      html += para(
        `At the written request of the Client, the Supplier shall perform the following additional services, which are outside the scope of the original Agreement:`,
      );
      html += `<ul style="margin:5px 0 10px 24px;padding:0;">`;
      addedScopes.forEach((s) => {
        html += `<li style="font-size:15px;margin-bottom:4px;">${s.description}</li>`;
        //color:#166534
      });
      html += `</ul>`;
    }

    if (removedScopes.length > 0) {
      html += para(
        `The following items are removed from the scope of the original Agreement:`,
      );
      html += `<ul style="margin:5px 0 10px 24px;padding:0;">`;
      removedScopes.forEach((s) => {
        html += `<li style="font-size:15px;margin-bottom:4px;">${s.description}</li>`;
        //color:#991b1b;
      });
      html += `</ul>`;
    }

    html += para(
      `These services shall be delivered in accordance with the same standards and obligations defined in the original Contract.`,
    );
    html += para(`&nbsp;`);
    html += para(`<strong>Description of Change</strong>`);
    html += para(
      `At the Client&apos;s request, Zithas Technologies Ltd will provide the functionality described above. All additional services will be delivered in accordance with the quality standards and delivery obligations set out in the original Contract.`,
    );
  } else {
    // No scope items — still include a description section so user can write freely
    html += sectionHeading(`${S()}. Description of Change`);
    html += para(
      `At the Client&apos;s request, Zithas Technologies Ltd will provide the following additional functionality.`,
    );
    html += para(
      `All additional services will be delivered in accordance with the quality standards and delivery obligations set out in the original Contract.`,
    );
  }

  // ── Commercial Variation ──
  if (feeChange || hoursChange) {
    html += sectionHeading(`${S()}. Commercial Variation`);
    html += para(
      `The Parties agree that the above change in scope results in the following commercial adjustment:`,
    );
    html += `<table style="width:100%;border-collapse:collapse;margin:8px 0 12px 0;">`;
    html += tableHeader("Description", "Value");
    html += `<tbody>`;
    if (hoursChange)
      html += row("Additional Development Effort", `${hoursNum} Hours`);
    if (feeChange)
      html += row(
        feeDirection === "add"
          ? "Additional Contract Value"
          : "Reduction in Contract Value",
        `${
          feeDirection === "remove" ? "−" : ""
        }${sym}${feeNum.toLocaleString()}`,
      );
    html += `</tbody></table>`;
    html += para(
      `The additional amount shall be invoiced and paid in accordance with the payment terms set out in the original Agreement.`,
    );
  }

  // ── Programme Impact ──
  if (validMilestones.length > 0) {
    html += sectionHeading(`${S()}. Programme Impact`);
    html += para(
      `The Parties acknowledge that the additional work will affect the delivery schedule as follows:`,
    );
    html += `<table style="width:100%;border-collapse:collapse;margin:8px 0 12px 0;">`;
    html += `<thead><tr>
      <th style="background:#f2f2f2;border:1px solid #ccc;padding:7px 12px;font-size:14px;text-transform:uppercase;letter-spacing:0.03em;text-align:left;">Milestone / Deliverable</th>
      <th style="background:#f2f2f2;border:1px solid #ccc;padding:7px 12px;font-size:14px;text-transform:uppercase;letter-spacing:0.03em;text-align:left;">Revised Date</th>
    </tr></thead><tbody>`;
    validMilestones.forEach((m) => {
      html += `<tr>
        <td style="padding:7px 12px;border:1px solid #ccc;font-size:15px;">${
          m.name || "—"
        }</td>
        <td style="padding:7px 12px;border:1px solid #ccc;font-size:15px;">${
          m.revised_date ? moment(m.revised_date).format("DD MMMM YYYY") : "—"
        }</td>
      </tr>`;
    });
    html += `</tbody></table>`;
  }

  // ── Revised Contract Position ──
  if (feeChange || (hoursChange && currentHours > 0)) {
    html += sectionHeading(`${S()}. Revised Contract Position`);
    html += para(
      `Following execution of this Amendment, the Contract shall stand amended as follows:`,
    );

    if (feeChange) {
      html += `<p style="font-size:15px;font-weight:700;margin:10px 0 5px 0;">Contract Value</p>`;
      html += `<table style="width:100%;border-collapse:collapse;margin:0 0 14px 0;">`;
      html += tableHeader("Description", "Amount");
      html += `<tbody>`;
      html += row(
        "Original Contract Value",
        `${sym}${contractValue.toLocaleString()}`,
      );
      html += row(
        `Value of Amendment ${amendLabel}`,
        `${
          feeDirection === "remove" ? "−" : ""
        }${sym}${feeNum.toLocaleString()}`,
      );
      html += summaryRow(
        "Revised Contract Value",
        `${sym}${revisedValue.toLocaleString()}`,
      );
      html += `</tbody></table>`;
    }

    if (hoursChange && currentHours > 0) {
      html += `<p style="font-size:15px;font-weight:700;margin:12px 0 5px 0;">Development Effort</p>`;
      html += `<table style="width:100%;border-collapse:collapse;margin:0 0 14px 0;">`;
      html += tableHeader("Description", "Hours");
      html += `<tbody>`;
      html += row("Original Development Hours", `${currentHours}`);
      html += row(
        hoursDirection === "add"
          ? "Additional Development Hours"
          : "Reduction in Development Hours",
        `${hoursNum}`,
      );
      html += summaryRow("Revised Development Hours", `${revisedHours}`);
      html += `</tbody></table>`;
    }
  }

  // ── Effect ──
  html += sectionHeading(`${S()}. Effect of this Amendment`);
  html += para(
    `This Amendment forms an integral part of the original Contract.`,
  );
  html += para(
    `In the event of any inconsistency between this Amendment and the original Contract, the provisions of this Amendment shall prevail solely in respect of the matters expressly amended herein.`,
  );

  // ── Acceptance & Signatures ──
  html += sectionHeading(`${S()}. Acceptance &amp; Signatures`);
  html += para(
    `By signing below, the Parties acknowledge that they have read, understood and agreed to the terms of this Amendment.`,
  );

  //   html += `
  //   <table style="width:100%;border-collapse:collapse;margin-top:16px;">
  //     <tbody>
  //       <tr>
  //         <td style="width:48%;vertical-align:top;border:1px solid #ccc;padding:16px 18px;">
  //           <p style="font-size:15px;font-weight:700;margin:0 0 12px 0;">For and on behalf of<br/>Zithas Technologies Ltd</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Name:</p>
  //           <p style="border-bottom:1px solid #888;margin:0 0 12px 0;min-height:20px;">&nbsp;</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Position:</p>
  //           <p style="border-bottom:1px solid #888;margin:0 0 12px 0;min-height:20px;">&nbsp;</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Signature:</p>
  //           <p style="margin:5px 0 12px 0;"><img src="${sign}" height="34" alt="Zithas signature" /></p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Date:</p>
  //           <p style="border-bottom:1px solid #888;margin:0;min-height:20px;">&nbsp;</p>
  //         </td>
  //         <td style="width:4%;">&nbsp;</td>
  //         <td style="width:48%;vertical-align:top;border:1px solid #ccc;padding:16px 18px;">
  //           <p style="font-size:15px;font-weight:700;margin:0 0 12px 0;">For and on behalf of<br/>${contract?.company_name || "[Client Name]"}</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Name:</p>
  //           <p style="border-bottom:1px solid #888;margin:0 0 12px 0;min-height:20px;">&nbsp;</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Position:</p>
  //           <p style="border-bottom:1px solid #888;margin:0 0 12px 0;min-height:20px;">&nbsp;</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Signature:</p>
  //           <p style="margin:5px 0 12px 0;border:1px dashed #bbb;height:34px;display:flex;align-items:center;justify-content:center;color:#aaa;font-size:13px;">Client to sign</p>
  //           <p style="font-size:14px;margin:0 0 5px 0;">Date:</p>
  //           <p style="border-bottom:1px solid #888;margin:0;min-height:20px;">&nbsp;</p>
  //         </td>
  //       </tr>
  //     </tbody>
  //   </table>
  // `;

  html += `</div>`;
  return html;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const AddContractAmendment = () => {
  // const { id: contractId } = useParams();
  const { id: contractId, amendmentId } = useParams();
const isEditMode = Boolean(amendmentId);
  const navigate = useNavigate();

  const [contract, setContract] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [fixButtons, setFixButtons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [amendmentNo] = useState(1);

  // ── Form fields (left panel) ──
  const [changeReason, setChangeReason] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [hoursDirection, setHoursDirection] = useState("add");
  const [hoursChange, setHoursChange] = useState("");
  const [feeDirection, setFeeDirection] = useState("add");
  const [feeChange, setFeeChange] = useState("");
  const [milestones, setMilestones] = useState([emptyMilestone()]);
  const [scopeItems, setScopeItems] = useState([emptyScope()]);
  const [errors, setErrors] = useState({});

  // ── Editor state ──
  const editorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const [manualMode, setManualMode] = useState(false); // true = user has edited; form no longer auto-injects
  //const [lastGenerated,setLastGenerated]= useState("");      // last HTML we pushed into the editor
  const lastGeneratedRef = useRef("");
  const [customChangeReason, setCustomChangeReason] = useState("");

  const [editAmendmentData, setEditAmendmentData] = useState(null);

  // document.title = "Create Amendment | Zithas CRM";
  document.title = isEditMode ? "Edit Amendment | Zithas CRM" : "Create Amendment | Zithas CRM";

  // ── Derived financial values ──
  const contractValue = parseFloat(contract?.value) || 0;
  const currentHours = parseFloat(contract?.hours) || 0;
  const feeNum = parseFloat(feeChange) || 0;
  const hoursNum = parseFloat(hoursChange) || 0;
  const revisedValue =
    feeDirection === "add" ? contractValue + feeNum : contractValue - feeNum;
  const revisedHours =
    hoursDirection === "add"
      ? currentHours + hoursNum
      : currentHours - hoursNum;
  const sym = contract?.currency_symbol || "£";

  // ── Scroll watcher ──
  useEffect(() => {
    const fn = () => setFixButtons(window.scrollY > 160);
    document.addEventListener("scroll", fn);
    return () => document.removeEventListener("scroll", fn);
  }, []);

  // ── Fetch contract ──
  useEffect(() => {
    if (contractId) fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    setLoading(true);
    const fd = new FormData();
    fd.append("contract_id", contractId);
    try {
      const res = await axios({
        method: "post",
        url: `${configure.nodeApi}/view_contract`,
        data: fd,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      setContract(res.data.data[0]);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", text: "Failed to load contract data." });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
  if (isEditMode && amendmentId) fetchAmendmentForEdit();
}, [amendmentId]);

const fetchAmendmentForEdit = async () => {
  setLoading(true);
  try {
    const res = await axios.get(
      `${configure.nodeApi}/contract_amendment/view/${amendmentId}`
    );
    if (res?.data?.success) {
      const a = res.data.data;
      setEditAmendmentData(a);

      // prefill contract_id so fetchContract runs
      // contractId from params will be undefined in edit mode, so we use a.contract_id
      const fd = new FormData();
      fd.append("contract_id", a.contract_id);
      const contractRes = await axios({
        method: "post",
        url: `${configure.nodeApi}/view_contract`,
        data: fd,
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      setContract(contractRes.data.data[0]);

      // prefill form fields
      const matchedReason = CHANGE_REASON_OPTIONS.find(
        (o) => o.value === a.change_reason
      );
      if (matchedReason) {
        setChangeReason(matchedReason);
      } else if (a.change_reason) {
        // custom reason (other)
        setChangeReason({ label: "Other", value: "other" });
        setCustomChangeReason(a.change_reason);
      }

      setEffectiveDate(a.effective_date || "");
      setHoursDirection(a.hours_direction || "add");
      setHoursChange(a.hours_change ? String(a.hours_change) : "");
      setFeeDirection(a.fee_direction || "add");
      setFeeChange(a.fee_change ? String(a.fee_change) : "");

      // scope items
      let parsedScope = [];
      try {
        parsedScope = typeof a.scope_items === "string"
          ? JSON.parse(a.scope_items)
          : (a.scope_items || []);
        if (!Array.isArray(parsedScope)) parsedScope = [];
      } catch { parsedScope = []; }
      setScopeItems(parsedScope.length ? parsedScope : [emptyScope()]);

      // milestones — if revised_end_date exists, pre-populate first milestone
      if (a.revised_end_date) {
        setMilestones([{ name: "Project delivery", revised_date: a.revised_end_date }]);
      }

      // inject existing document_html into editor once it's ready
      // (handled in Change 4 below)
    }
  } catch (err) {
    console.error(err);
    Swal.fire({ icon: "error", text: "Failed to load amendment data." });
  } finally {
    setLoading(false);
  }
};

  // ── Build & push document into editor ──
  const buildDocPayload = useCallback(
    () => ({
      contract,
      amendmentNo,
      changeReason: changeReason
        ? {
            ...changeReason,
            label:
              changeReason.value === "other"
                ? customChangeReason || "Other"
                : changeReason.label,
          }
        : null,
      effectiveDate,
      hoursDirection,
      hoursChange,
      feeDirection,
      feeChange,
      revisedValue,
      revisedHours,
      milestones,
      scopeItems,
      sym,
    }),
    [
      contract,
      amendmentNo,
      changeReason,
      customChangeReason,
      effectiveDate,
      hoursDirection,
      hoursChange,
      feeDirection,
      feeChange,
      revisedValue,
      revisedHours,
      milestones,
      scopeItems,
      sym,
    ],
  );

  // const injectDocument = useCallback((force = false) => {
  //   if (!editorRef.current || !contract) return;
  //   if (manualMode && !force) return;      // don't overwrite user's manual edits
  //   const html = generateDocumentHTML(buildDocPayload());
  //   setLastGenerated(html);
  //   editorRef.current.setContent(html);
  // }, [contract, manualMode, buildDocPayload]);

  const injectDocument = useCallback(
    (force = false) => {
      if (!editorRef.current || !contract) return;
      if (manualMode && !force) return;
      const html = generateDocumentHTML(buildDocPayload());
      lastGeneratedRef.current = html; // sync — already set before setContent fires
      editorRef.current.setContent(html);
    },
    [contract, manualMode, buildDocPayload],
  );

  // Auto-inject when editor is ready (first load)
  // useEffect(() => {
  //   if (editorReady && contract && !manualMode) injectDocument();
  // }, [editorReady, contract]); // intentionally only on editorReady + contract load
  useEffect(() => {
  if (!editorReady || !contract) return;
  if (isEditMode && editAmendmentData?.document_html) {
    // edit mode: load the existing saved HTML
    editorRef.current.setContent(editAmendmentData.document_html);
    setManualMode(true); // treat as manually edited so auto-inject doesn't overwrite
  } else if (!manualMode) {
    // create mode: auto-generate
    injectDocument();
  }
}, [editorReady, contract, editAmendmentData]);

  // ── Validate ──
  const validate = () => {
    const e = {};
    // if (!changeReason)  e.changeReason  = "Change reason is required";
    if (!changeReason) e.changeReason = "Change reason is required";
    else if (changeReason.value === "other" && !customChangeReason.trim())
      e.changeReason = "Please specify the reason";
    if (!effectiveDate) e.effectiveDate = "Effective date is required";
    if (!editorRef.current?.getContent()?.trim())
      e.editor = "Amendment document cannot be empty";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save ──
  
  // const handleSave = async (ev) => {
  //   ev.preventDefault();
  //   if (!validate()) return;

  //   const finalHtml = editorRef.current.getContent();

  //   const formEntries = {
  //     contract_id: contractId,
  //     user_id: localStorage.getItem("auth_id"),
  //     document_html: finalHtml, // full TinyMCE document HTML
  //     //change_reason:    changeReason?.value,
  //     change_reason:
  //       changeReason?.value === "other"
  //         ? customChangeReason
  //         : changeReason?.value,
  //     effective_date: effectiveDate,
  //     hours_direction: hoursDirection,
  //     hours_change: hoursNum,
  //     fee_direction: feeDirection,
  //     fee_change: feeNum,
  //     revised_end_date:
  //       milestones.find((m) => m.revised_date)?.revised_date || "",
  //     scope_items: scopeItems
  //       .filter((s) => s.description)
  //       .map((s) => ({ type: s.type, description: s.description })),
  //   };

  //   setIsSaving(true);
  //   try {
  //     const res = await FetchNodePostData(
  //       setIsSaving,
  //       "contract_amendment",
  //       secureLocalStorage.getItem("auth_token"),
  //       formEntries,
  //     );

  //     if (res?.data?.success) {
  //       Swal.fire({
  //         icon: "success",
  //         text: "Amendment created and sent for internal approval.",
  //         timer: 2500,
  //         showConfirmButton: false,
  //       }).then(() => navigate(`${configure.appUrl}sales/contract`));
  //     } else {
  //       Swal.fire({
  //         icon: "error",
  //         text: res?.data?.message || "Something went wrong.",
  //       });
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     Swal.fire({
  //       icon: "error",
  //       text: "Something went wrong. Please try again.",
  //     });
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleSave = async (ev) => {
  ev.preventDefault();
  if (!validate()) return;

  const finalHtml = editorRef.current.getContent();
  const formEntries = {
    user_id:         localStorage.getItem("auth_id"),
    document_html:   finalHtml,
    change_reason:   changeReason?.value === "other" ? customChangeReason : changeReason?.value,
    effective_date:  effectiveDate,
    hours_direction: hoursDirection,
    hours_change:    hoursNum,
    fee_direction:   feeDirection,
    fee_change:      feeNum,
    revised_end_date: milestones.find((m) => m.revised_date)?.revised_date || "",
    scope_items:     scopeItems
      .filter((s) => s.description)
      .map((s) => ({ type: s.type, description: s.description })),
  };

  setIsSaving(true);
  try {
    let res;
    if (isEditMode) {
      // EDIT — PUT to edit endpoint
      res = await axios.put(
        `${configure.nodeApi}/contract_amendment/edit/${amendmentId}`,
        formEntries
      );
      res = { data: res.data }; // normalise shape
    } else {
      // CREATE — existing flow unchanged
      formEntries.contract_id = contractId;
      res = await FetchNodePostData(
        setIsSaving,
        "contract_amendment",
        secureLocalStorage.getItem("auth_token"),
        formEntries,
      );
    }

    if (res?.data?.success) {
      Swal.fire({
        icon: "success",
        text: isEditMode
          ? "Amendment updated and reset for internal approval."
          : "Amendment created and sent for internal approval.",
        timer: 2500,
        showConfirmButton: false,
      }).then(() => navigate(-1));
    } else {
      Swal.fire({ icon: "error", text: res?.data?.message || "Something went wrong." });
    }
  } catch (err) {
    console.error(err);
    Swal.fire({ icon: "error", text: "Something went wrong. Please try again." });
  } finally {
    setIsSaving(false);
  }
};

  // ── Regenerate (force refresh from form) ──
  const handleRegenerate = () => {
    setManualMode(false);
    // small tick to let state settle
    setTimeout(() => injectDocument(true), 0);
  };

  // ── Milestone helpers ──
  const updateMilestone = (idx, field, val) => {
    const u = [...milestones];
    u[idx][field] = val;
    setMilestones(u);
  };
  const addMilestone = () => setMilestones([...milestones, emptyMilestone()]);
  const removeMilestone = (idx) =>
    setMilestones(milestones.filter((_, i) => i !== idx));

  // ── Scope helpers ──
  const updateScope = (idx, field, val) => {
    const u = [...scopeItems];
    u[idx][field] = val;
    setScopeItems(u);
  };
  const addScope = () => setScopeItems([...scopeItems, emptyScope()]);
  const removeScope = (idx) =>
    setScopeItems(scopeItems.filter((_, i) => i !== idx));

  // add inside the component, near other useMemo/derived values
  const contractScopeOptions = React.useMemo(() => {
    if (!contract?.project_scope) return [];
    return contract.project_scope
      .split("@#")
      .filter(Boolean)
      .map((item) => ({ label: item, value: item }));
  }, [contract]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      {isLoading && <Loader />}
      <div className="page-content mb-3">
        <Container fluid>
          {/* <Breadcrumbs title="Contract" breadcrumbItem="Create Amendment" /> */}
          <Breadcrumbs
  title="Contract"
  breadcrumbItem={isEditMode ? "Edit Amendment" : "Create Amendment"}
/>

          {/* Fixed floating buttons */}
          <div
            className={`flex-column fixed-buttons pt-2 ${
              fixButtons ? "d-flex" : "d-none"
            }`}
          >
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-save fix-btn btn-label pe-0"
            >
              <i className="fas fa-paper-plane me-2 label-icon" />
              <span>{isSaving ? "Saving…" : "Save"}</span>
            </button>
            <a
              onClick={() => navigate(-1)}
              className="btn btn-red fix-btn btn-label pe-0"
            >
              <i className="dripicons-cross me-2 icon-center label-icon" />
              <span>Cancel</span>
            </a>
          </div>

          <Form onSubmit={handleSave}>
            <Row className="px-1 g-3">
              {/* ══════════════════════════════════════════════════════════
                  LEFT — Structured form fields
              ══════════════════════════════════════════════════════════ */}
              <div className="col-lg-5">
                {/* Info banner */}
                <div
                  className="d-flex align-items-start gap-2 p-3 mb-3 rounded"
                  style={{
                    background: "rgba(255,193,7,0.08)",
                    border: "0.5px solid rgba(255,193,7,0.4)",
                    fontSize: "12.5px",
                    color: "#856404",
                  }}
                >
                  <i className="fas fa-info-circle mt-1 flex-shrink-0" />
                  <span>
                    Fill in the fields below — the document on the right updates
                    automatically. Once you start editing the document directly,
                    it enters <strong>manual mode</strong> and form changes will
                    no longer auto-update it (use{" "}
                    <strong>Refresh Document</strong> to re-sync).
                  </span>
                </div>

                {/* ── Amendment Details ── */}
                <SectionCard title="Amendment Details" icon="fas fa-pencil-alt">
                  <Row>
                    <div className="col-md-6 mb-3">
                      <FieldLabel required>Change Reason</FieldLabel>
                      <Select
                        options={CHANGE_REASON_OPTIONS}
                        value={changeReason}
                        onChange={(v) => {
                          setChangeReason(v);
                          setErrors((p) => ({ ...p, changeReason: "" }));
                          if (!manualMode)
                            setTimeout(() => injectDocument(true), 0);
                        }}
                        placeholder="Select reason"
                        classNamePrefix="select"
                        styles={colourStyles}
                      />
                      {errors.changeReason && (
                        <div
                          className="text-danger mt-1"
                          style={{ fontSize: "12px" }}
                        >
                          {errors.changeReason}
                        </div>
                      )}
                    </div>
                    {changeReason?.value === "other" && (
                      <Row>
                        <div className="col-12 mb-3">
                          <FieldLabel required>
                            Please specify reason
                          </FieldLabel>
                          <input
                            type="text"
                            className={`form-control${
                              errors.changeReason ? " is-invalid" : ""
                            }`}
                            placeholder="Type the reason for this amendment"
                            value={customChangeReason}
                            onChange={(e) => {
                              setCustomChangeReason(e.target.value);
                              setErrors((p) => ({ ...p, changeReason: "" }));
                              if (!manualMode)
                                setTimeout(() => injectDocument(true), 50);
                            }}
                            onBlur={() => {
                              if (!manualMode) injectDocument(true);
                            }}
                          />
                          {errors.changeReason && (
                            <div className="invalid-feedback d-block">
                              {errors.changeReason}
                            </div>
                          )}
                        </div>
                      </Row>
                    )}

                    <div className="col-md-6 mb-3">
                      <FieldLabel required>Effective Date</FieldLabel>
                      <input
                        type="date"
                        className={`form-control${
                          errors.effectiveDate ? " is-invalid" : ""
                        }`}
                        value={effectiveDate}
                        onChange={(e) => {
                          setEffectiveDate(e.target.value);
                          setErrors((p) => ({ ...p, effectiveDate: "" }));
                          if (!manualMode)
                            setTimeout(() => injectDocument(true), 0);
                        }}
                      />
                      {errors.effectiveDate && (
                        <div className="invalid-feedback">
                          {errors.effectiveDate}
                        </div>
                      )}
                    </div>
                  </Row>
                </SectionCard>

                {/* ── Scope ── */}
                <SectionCard
                  title="Scope Additions / Removals"
                  icon="fas fa-list-ul"
                >
                  {scopeItems.map((s, idx) => (
                    <div
                      key={idx}
                      className="d-flex gap-2 mb-2 align-items-end"
                    >
                      <div>
                        {idx === 0 && <FieldLabel>Type</FieldLabel>}
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              s.type === "add"
                                ? "btn-success"
                                : "btn-outline-secondary"
                            }`}
                            style={{ fontSize: "11px", padding: "5px 10px" }}
                            onClick={() => {
                              updateScope(idx, "type", "add");
                              if (!manualMode)
                                setTimeout(() => injectDocument(true), 50);
                            }}
                          >
                            + Add
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              s.type === "remove"
                                ? "btn-danger"
                                : "btn-outline-secondary"
                            }`}
                            style={{ fontSize: "11px", padding: "5px 10px" }}
                            onClick={() => {
                              updateScope(idx, "type", "remove");
                              if (!manualMode)
                                setTimeout(() => injectDocument(true), 50);
                            }}
                          >
                            − Remove
                          </button>
                        </div>
                      </div>

                      {/* <div style={{ flex: 1 }}>
  {idx === 0 && <FieldLabel>Scope Description</FieldLabel>}
  {s.type === "remove" ? (
    <Select
      options={contractScopeOptions.filter(
        (opt) =>
          !scopeItems.some(
            (si, i) => i !== idx && si.type === "remove" && si.description === opt.value
          )
      )}
      value={s.description ? { label: s.description, value: s.description } : null}
      onChange={(sel) => {
        updateScope(idx, "description", sel ? sel.value : "");
        if (!manualMode) setTimeout(() => injectDocument(true), 50);
      }}
      placeholder="Select item to remove"
      classNamePrefix="select"
      styles={colourStyles}
      isClearable
    />
  ) : (
    <input
      type="text"
      className="form-control"
      placeholder="e.g. Management Reporting Dashboard"
      value={s.description}
      onChange={(e) => updateScope(idx, "description", e.target.value)}
      onBlur={() => { if (!manualMode) injectDocument(true); }}
      style={{ fontSize: "13px" }}
    />
  )}
</div> */}
                      <div style={{ flex: 1 }}>
                        {idx === 0 && (
                          <FieldLabel>Scope Description</FieldLabel>
                        )}
                        {s.type === "remove" ? (
                          <CreatableSelect
                            options={contractScopeOptions.filter(
                              (opt) =>
                                !scopeItems.some(
                                  (si, i) =>
                                    i !== idx &&
                                    si.type === "remove" &&
                                    si.description === opt.value,
                                ),
                            )}
                            value={
                              s.description
                                ? { label: s.description, value: s.description }
                                : null
                            }
                            onChange={(sel) => {
                              updateScope(
                                idx,
                                "description",
                                sel ? sel.value : "",
                              );
                              if (!manualMode)
                                setTimeout(() => injectDocument(true), 50);
                            }}
                            onCreateOption={(inputValue) => {
                              updateScope(idx, "description", inputValue);
                              if (!manualMode)
                                setTimeout(() => injectDocument(true), 50);
                            }}
                            placeholder="Select or type item to remove"
                            formatCreateLabel={(inputValue) =>
                              `Add "${inputValue}" (not in original scope)`
                            }
                            classNamePrefix="select"
                            styles={colourStyles}
                            isClearable
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Management Reporting Dashboard"
                            value={s.description}
                            onChange={(e) =>
                              updateScope(idx, "description", e.target.value)
                            }
                            onBlur={() => {
                              if (!manualMode) injectDocument(true);
                            }}
                            style={{ fontSize: "13px" }}
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        style={{
                          marginBottom: "1px",
                          height: "36px",
                          width: "36px",
                          padding: 0,
                        }}
                        onClick={() => {
                          removeScope(idx);
                          if (!manualMode)
                            setTimeout(() => injectDocument(true), 50);
                        }}
                        disabled={scopeItems.length === 1}
                      >
                        <i
                          className="fas fa-trash-alt"
                          style={{ fontSize: "11px" }}
                        />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2 mb-1"
                    style={{ fontSize: "12px" }}
                    onClick={() => {
                      addScope();
                    }}
                  >
                    <i className="fas fa-plus me-1" /> Add Scope Item
                  </button>
                </SectionCard>

                {/* ── Financial & Hours ── */}
                <SectionCard
                  title="Financial & Hours Changes"
                  icon="fas fa-coins"
                >
                  <Row>
                    <div className="col-md-6 mb-3">
                      <FieldLabel>Hours Change</FieldLabel>
                      <div className="d-flex gap-2">
                        <DirectionToggle
                          value={hoursDirection}
                          onChange={(v) => {
                            setHoursDirection(v);
                            if (!manualMode)
                              setTimeout(() => injectDocument(true), 50);
                          }}
                        />
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          min="0"
                          value={hoursChange}
                          onChange={(e) => setHoursChange(e.target.value)}
                          onBlur={() => {
                            if (!manualMode) injectDocument(true);
                          }}
                          style={{ fontSize: "13px" }}
                        />
                      </div>
                      {hoursChange && currentHours > 0 && (
                        <div
                          className="mt-1"
                          style={{ fontSize: "12px", color: "#556ee6" }}
                        >
                          {currentHours} hrs →{" "}
                          <strong>{revisedHours} hrs</strong>
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <FieldLabel>Fee Change</FieldLabel>
                      <div className="d-flex gap-2">
                        <DirectionToggle
                          value={feeDirection}
                          onChange={(v) => {
                            setFeeDirection(v);
                            if (!manualMode)
                              setTimeout(() => injectDocument(true), 50);
                          }}
                        />
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0.00"
                          min="0"
                          value={feeChange}
                          onChange={(e) => setFeeChange(e.target.value)}
                          onBlur={() => {
                            if (!manualMode) injectDocument(true);
                          }}
                          style={{ fontSize: "13px" }}
                        />
                      </div>
                      {feeChange && (
                        <div
                          className="mt-1"
                          style={{ fontSize: "12px", color: "#556ee6" }}
                        >
                          {sym}
                          {contractValue.toLocaleString()} →{" "}
                          <strong>
                            {sym}
                            {revisedValue.toLocaleString()}
                          </strong>
                        </div>
                      )}
                    </div>

                    {(feeChange || (hoursChange && currentHours > 0)) && (
                      <div className="col-12 mb-2">
                        <div
                          className="d-flex gap-4 p-2 rounded"
                          style={{
                            background: "rgba(85,110,230,0.06)",
                            border: "0.5px solid rgba(85,110,230,0.2)",
                            fontSize: "13px",
                          }}
                        >
                          {feeChange && (
                            <span>
                              <span style={{ color: "#74788d" }}>
                                Revised value:{" "}
                              </span>
                              <strong
                                style={{
                                  color:
                                    feeDirection === "add"
                                      ? "#34c38f"
                                      : "#f46a6a",
                                }}
                              >
                                {sym}
                                {revisedValue.toLocaleString()}
                              </strong>
                            </span>
                          )}
                          {hoursChange && currentHours > 0 && (
                            <span>
                              <span style={{ color: "#74788d" }}>
                                Revised hours:{" "}
                              </span>
                              <strong
                                style={{
                                  color:
                                    hoursDirection === "add"
                                      ? "#34c38f"
                                      : "#f46a6a",
                                }}
                              >
                                {revisedHours} hrs
                              </strong>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </Row>
                </SectionCard>

                {/* ── Milestones ── */}
                <SectionCard
                  title="Revised Milestone Dates"
                  icon="fas fa-calendar-alt"
                >
                  {milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className="d-flex gap-2 mb-2 align-items-end"
                    >
                      <div style={{ flex: 1 }}>
                        {idx === 0 && (
                          <FieldLabel>Milestone / Deliverable</FieldLabel>
                        )}
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Project delivery"
                          value={m.name}
                          onChange={(e) =>
                            updateMilestone(idx, "name", e.target.value)
                          }
                          onBlur={() => {
                            if (!manualMode) injectDocument(true);
                          }}
                          style={{ fontSize: "13px" }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        {idx === 0 && <FieldLabel>Revised Date</FieldLabel>}
                        <input
                          type="date"
                          className="form-control"
                          value={m.revised_date}
                          onChange={(e) => {
                            updateMilestone(
                              idx,
                              "revised_date",
                              e.target.value,
                            );
                            if (!manualMode)
                              setTimeout(() => injectDocument(true), 50);
                          }}
                          style={{ fontSize: "13px" }}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        style={{
                          marginBottom: "1px",
                          height: "36px",
                          width: "36px",
                          padding: 0,
                        }}
                        onClick={() => {
                          removeMilestone(idx);
                          if (!manualMode)
                            setTimeout(() => injectDocument(true), 50);
                        }}
                        disabled={milestones.length === 1}
                      >
                        <i
                          className="fas fa-trash-alt"
                          style={{ fontSize: "11px" }}
                        />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2 mb-1"
                    style={{ fontSize: "12px" }}
                    onClick={() => addMilestone()}
                  >
                    <i className="fas fa-plus me-1" /> Add Milestone
                  </button>
                </SectionCard>

                {/* ── Footer ── */}
                <div className="bg-white px-4 py-3 mb-3 d-flex justify-content-end gap-2">
                  <a
                    onClick={() => navigate(-1)}
                    className="btn btn-red btn-label"
                  >
                    <i className="dripicons-cross me-2 icon-center label-icon" />{" "}
                    Cancel
                  </a>
                  <button
                    type="submit"
                    className="btn btn-save btn-label"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <i className="bx bx-loader bx-spin label-icon" />{" "}
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-1 label-icon" />{" "}
                        Save &amp; Send to Client
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* ══════════════════════════════════════════════════════════
                  RIGHT — Single TinyMCE document editor
              ══════════════════════════════════════════════════════════ */}
              <div className="col-lg-7">
                <div
                  className="bg-white shadow-sm"
                  style={{
                    position: "sticky",
                    top: "80px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  {/* Header bar */}
                  <div
                    className="d-flex align-items-center justify-content-between px-3 py-2 flex-shrink-0 flex-wrap gap-2"
                    style={{
                      borderBottom: "1px solid #eff2f7",
                      background: "#fafafa",
                      minHeight: "48px",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <i
                        className="fas fa-file-contract text-primary"
                        style={{ fontSize: "13px" }}
                      />
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#495057",
                        }}
                      >
                        Amendment Document
                      </span>

                      {contract?.company_name && (
                        <span
                          className="badge"
                          style={{
                            background: "rgba(85,110,230,0.1)",
                            color: "#556ee6",
                            fontSize: "11px",
                            fontWeight: 400,
                          }}
                        >
                          {contract.company_name}
                        </span>
                      )}

                      {/* Mode badge */}
                      {manualMode ? (
                        <span
                          className="badge d-flex align-items-center gap-1"
                          style={{
                            background: "rgba(244,106,106,0.12)",
                            color: "#f46a6a",
                            fontSize: "11px",
                            fontWeight: 500,
                          }}
                        >
                          <i
                            className="fas fa-pencil-alt"
                            style={{ fontSize: "9px" }}
                          />{" "}
                          Manual Edit
                        </span>
                      ) : (
                        <span
                          className="badge d-flex align-items-center gap-1"
                          style={{
                            background: "rgba(52,195,143,0.12)",
                            color: "#34c38f",
                            fontSize: "11px",
                            fontWeight: 500,
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "#34c38f",
                              display: "inline-block",
                            }}
                          />{" "}
                          Auto
                        </span>
                      )}
                    </div>

                    {/* Refresh button — always visible; resets manual mode */}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                      onClick={handleRegenerate}
                      title="Re-generate document from current form values"
                    >
                      <i className="fas fa-sync-alt me-1" /> Refresh Document
                    </button>
                  </div>

                  {/* Manual-mode warning banner */}
                  {manualMode && (
                    <div
                      className="d-flex align-items-start gap-2 px-3 py-2"
                      style={{
                        background: "rgba(244,106,106,0.06)",
                        borderBottom: "1px solid rgba(244,106,106,0.2)",
                        fontSize: "12px",
                        color: "#c0392b",
                      }}
                    >
                      <i
                        className="fas fa-exclamation-triangle mt-1 flex-shrink-0"
                        style={{ fontSize: "11px" }}
                      />
                      <span>
                        You&apos;ve edited the document directly. Form changes
                        will <strong>not</strong> auto-update the document.
                        Click <strong>Refresh Document</strong> to regenerate
                        from form values (your direct edits will be replaced).
                      </span>
                    </div>
                  )}

                  {errors.editor && (
                    <div
                      className="px-3 py-1"
                      style={{
                        background: "#fff3f3",
                        borderBottom: "1px solid #f5c6c6",
                        fontSize: "12px",
                        color: "#c0392b",
                      }}
                    >
                      <i className="fas fa-exclamation-circle me-1" />{" "}
                      {errors.editor}
                    </div>
                  )}

                  {/* TinyMCE — the document IS the editor */}
                  <div style={{ minHeight: "600px" }}>
                    <Editor
                      apiKey="w6fz9u4rgsouz7jqcyy4lflpolp0tjwkx1bzoc70ntetxwys"
                      onInit={(evt, editor) => {
                        editorRef.current = editor;
                        setEditorReady(true);
                      }}
                      // onEditorChange={(content, editor) => {
                      //   // If the content change wasn't triggered by our own setContent call, mark as manual
                      //   if (content !== lastGenerated) {
                      //     setManualMode(true);
                      //   }
                      //   setErrors((p) => ({ ...p, editor: "" }));
                      // }}
                      onEditorChange={(content) => {
                        if (content !== lastGeneratedRef.current) {
                          setManualMode(true);
                        }
                        // only re-render when there's actually an error to clear
                        setErrors((p) => (p.editor ? { ...p, editor: "" } : p));
                      }}
                      init={{
                        height: 720,
                        menubar: true,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "image",
                          "charmap",
                          "anchor",
                          "searchreplace",
                          "visualblocks",
                          "fullscreen",
                          "insertdatetime",
                          "media",
                          "table",
                          "wordcount",
                          "code",
                          "pagebreak",
                          "quickbars",
                        ],
                        toolbar:
                          "undo redo | blocks | " +
                          "bold italic underline strikethrough | forecolor backcolor | " +
                          "fontfamily fontsize | " +
                          "alignleft aligncenter alignright alignjustify | " +
                          "bullist numlist outdent indent | " +
                          "table | link image | removeformat | code | fullscreen",
                        toolbar_mode: "wrap",
                        block_formats:
                          "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3",
                        fontsize_formats:
                          "10pt 11pt 12pt 13pt 14pt 16pt 18pt 24pt",
                        font_family_formats:
                          "Arial=arial,helvetica,sans-serif; " +
                          "Times New Roman=times new roman,times,serif; " +
                          "Courier New=courier new,courier,monospace; " +
                          "Georgia=georgia,palatino,serif;",
                        content_style: `
                          body {
                            font-family: Arial, Helvetica, sans-serif;
                            font-size: 13px;
                            color: #333;
                            line-height: 1.7;
                            margin: 20px 28px;
                            max-width: 100%;
                          }
                          table { border-collapse: collapse; }
                          td, th { border: 1px solid #ccc; padding: 5px 10px; }
                          p { margin: 0 0 6px 0; }
                          ul, ol { margin: 4px 0 8px 22px; padding: 0; }
                          li { margin-bottom: 3px; }
                          hr { border: none; border-top: 1.5px solid #333; margin: 12px 0; }
                        `,
                        //                         content_style: `
                        //   body {
                        //     font-family: Arial, Helvetica, sans-serif;
                        //     font-size: 13px;
                        //     color: #333;
                        //     line-height: 1.7;
                        //     padding: 1in 1in;
                        //     max-width: 8.5in;
                        //     margin: 0 auto;
                        //     box-sizing: border-box;
                        //   }
                        //   table { border-collapse: collapse; }
                        //   td, th { border: 1px solid #ccc; padding: 7px 12px; }
                        //   p { margin: 0 0 8px 0; }
                        //   ul, ol { margin: 6px 0 12px 26px; padding: 0; }
                        //   li { margin-bottom: 5px; }
                        //   hr { border: none; border-top: 1.5px solid #333; margin: 16px 0; }
                        // `,
                        quickbars_selection_toolbar:
                          "bold italic underline | forecolor | bullist numlist | quicklink",
                        quickbars_insert_toolbar: false,
                        contextmenu: "link table",
                        branding: false,
                        resize: true,
                        statusbar: true,
                        elementpath: false,
                        wordcount_countregex: /[\w\u2019\x27\-\u00C0-\u1FFF]+/g,
                      }}
                    />
                  </div>
                </div>
              </div>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddContractAmendment;
